import { useEffect, useState } from "react";
import { Link, useActionData, useNavigation, useSearchParams } from "react-router";
import type { ActionFunctionArgs } from "react-router";
import { useQueries, useSuspenseQuery } from "@tanstack/react-query";
import type { Notification } from "@repo/shared-types";
import { api } from "@/lib/api";
import { requireSession } from "@/lib/guards";
import { invitationsSeenUpTo, markedRead, readUpTo, timeAgo, unreadOf } from "@/lib/inbox";
import { queries } from "@/lib/queries";
import { queryClient } from "@/lib/query";
import { answerInvitation } from "@/lib/invitations";
import { errorMessage, invalidateSession } from "@/lib/session";
import { Invitations } from "@/components/invitations";
import { Avatar, SpacePicture } from "@/components/avatar";
import { PageTitle } from "@/components/brand";
import { TopBar } from "@/components/navigation";
import { ErrorNote } from "@/components/form";
import { Bell } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { EmptyNote } from "@/components/empty-note";
import { Item, ItemActions, ItemContent, ItemDescription, ItemMedia, ItemTitle } from "@/components/ui/item";
import { cn } from "@/lib/utils";

/**
 * The inbox: one per person, across every space they belong to.
 *
 * On its first page, the invitations addressed to them — an invitee is not a
 * member yet, so an invitation is never a notification — and, for whoever
 * verifies somewhere, the events with results waiting. Then the
 * notifications, newest first.
 *
 * Opening it reads it: everything up to the newest line shown, so the
 * header's count clears and a notification written meanwhile stays unread.
 * The lines that were new stay marked for this visit. The invitations it
 * shows are seen the same way, and leave the count too.
 */

export async function inboxLoader({ request }: { request: Request }) {
  await requireSession(request);
  const cursor = new URL(request.url).searchParams.get("cursor");
  const shown = [queries.inbox(cursor), ...(cursor ? [] : [queries.waiting(), queries.invitations()])];

  // Drawn from the copy when there is one, and the fresh list it is read
  // again into is the one marked read (0075): marking the copy would leave a
  // line arriving with the fresh list unread, and the count would come back
  // for a line on the screen.
  const held = shown.every(({ queryKey }) => {
    const state = queryClient.getQueryState(queryKey);
    return state?.data !== undefined && !state.isInvalidated;
  });
  const reading = readAndMark(cursor);
  if (!held) {
    await reading;
    return null;
  }
  // A read that fails behind the copy leaves the copy, unmarked.
  reading.catch(() => {});
  return null;
}

/**
 * Reads the inbox afresh, then everything on it up to the newest line shown,
 * exactly as the api gave its `createdAt`, so a notification written since
 * stays unread; and sees the invitations up to the newest.
 */
async function readAndMark(cursor: string | null) {
  const [page, invitations] = await Promise.all([
    queryClient.fetchQuery(queries.inbox(cursor)),
    cursor ? null : queryClient.fetchQuery(queries.invitations()),
    cursor ? null : queryClient.fetchQuery(queries.waiting()),
  ]);
  const upTo = readUpTo(page);
  const invitationsUpTo = invitations ? invitationsSeenUpTo(invitations.invitations) : null;
  if (!upTo && !invitationsUpTo) return;

  await api.inbox.read({ ...(upTo && { upTo }), ...(invitationsUpTo && { invitationsUpTo }) });
  // The count is on the session.
  invalidateSession();
}

type ActionResult = { error?: string };

export async function inboxAction({ request }: ActionFunctionArgs): Promise<ActionResult | Response> {
  const form = await request.formData();
  try {
    return (await answerInvitation(String(form.get("intent") ?? ""), form)) ?? { error: "Unknown action" };
  } catch (error) {
    return { error: errorMessage(error, "Something went wrong") };
  }
}

export default function Inbox() {
  const cursor = useSearchParams()[0].get("cursor");
  const { data: page } = useSuspenseQuery(queries.inbox(cursor));
  const lists = useQueries({
    queries: [
      { ...queries.waiting(), enabled: !cursor },
      { ...queries.invitations(), enabled: !cursor },
    ],
  });
  const waiting = lists[0].data?.waiting ?? [];
  const moreWaiting = Boolean(lists[0].data?.nextCursor);
  const invitations = lists[1].data?.invitations ?? [];
  const [now] = useState(() => Date.now());
  // What arrived unread stays marked new while the inbox is open, though
  // opening it has read it: the fresh list read behind a copy adds its own.
  const [fresh, setFresh] = useState(() => new Set(unreadOf(page)));
  useEffect(() => {
    const unread = unreadOf(page);
    setFresh((shown) => (unread.every((id) => shown.has(id)) ? shown : new Set([...shown, ...unread])));
  }, [page]);
  // Once left, its copy says what the api does — read, by opening it — so
  // the next visit draws nothing seen as new before its fresh list arrives.
  useEffect(
    () => () => {
      queryClient.setQueryData(queries.inbox(cursor).queryKey, (copy) => copy && markedRead(copy));
    },
    [cursor],
  );
  const result = useActionData() as ActionResult | undefined;
  const busy = useNavigation().state === "submitting";

  return (
    <section className="space-y-6">
      <PageTitle of={["Inbox"]} />
      <TopBar>
        <h1 className="page-title">Inbox</h1>
      </TopBar>

      {result?.error && <ErrorNote message={result.error} />}

      <Invitations invitations={invitations} busy={busy} />

      {waiting.length > 0 && (
        <div className="space-y-3">
          <h2 className="section-label">Waiting for verification</h2>
          <ul className="space-y-2">
            {waiting.map((line) => (
              <li key={line.event.id}>
                <Item variant="outline" className="bg-card">
                  <ItemMedia>
                    <SpacePicture space={line.space} />
                  </ItemMedia>
                  <ItemContent className="min-w-0 wrap-anywhere">
                    <ItemTitle className="line-clamp-2">
                      {line.results} {line.results === 1 ? "result" : "results"} · {line.event.title ?? "an event"}
                    </ItemTitle>
                    <ItemDescription>
                      {line.space.name} · waiting {timeAgo(line.since, now)}
                    </ItemDescription>
                  </ItemContent>
                  <ItemActions>
                    <Button asChild variant="outline" size="sm">
                      <Link to={line.link}>Verify →</Link>
                    </Button>
                  </ItemActions>
                </Item>
              </li>
            ))}
          </ul>
          {moreWaiting && (
            <p className="text-sm text-muted-foreground">These are the events that have waited longest; more wait too.</p>
          )}
        </div>
      )}

      <div className="space-y-3">
        {(invitations.length > 0 || waiting.length > 0) && (
          <h2 className="section-label">Notifications</h2>
        )}
        {page.notifications.length === 0 ? (
          <EmptyNote icon={Bell}>
            {cursor ? "Nothing older." : "Nothing yet. News from your spaces shows up here."}
          </EmptyNote>
        ) : (
          <Card className="gap-0 p-2">
            <ul aria-label="Notifications" className="space-y-1">
              {page.notifications.map((line) => (
                <li key={line.id}>
                  <Line line={line} isNew={fresh.has(line.id)} now={now} />
                </li>
              ))}
            </ul>
          </Card>
        )}

        <nav className="flex justify-between">
          {cursor ? (
            <Button asChild variant="outline" size="lg">
              <Link to="/inbox">← Newest</Link>
            </Button>
          ) : (
            <span />
          )}
          {page.nextCursor && (
            <Button asChild variant="outline" size="lg">
              <Link to={`/inbox?cursor=${encodeURIComponent(page.nextCursor)}`}>Older →</Link>
            </Button>
          )}
        </nav>
      </div>
    </section>
  );
}

/**
 * One notification: the api's sentence, when, and where it leads. The same
 * for every kind, so a kind this page has never heard of still reads.
 */
function Line({ line, isNew, now }: { line: Notification; isNew: boolean; now: number }) {
  return (
    <Item asChild size="sm" className={cn("items-start", isNew && "bg-muted/60")}>
      <Link to={line.link}>
        {/* Whoever did it, the space's picture on its corner — or the space's
            alone, for a verification or a hiding, which name nobody. */}
        <ItemMedia>
          {line.actor ? (
            <span className="relative">
              <Avatar person={line.actor} size="md" />
              <SpacePicture space={line.space} size="xs" className="absolute -right-1 -bottom-1 ring-2 ring-card" />
            </span>
          ) : (
            <SpacePicture space={line.space} size="md" />
          )}
        </ItemMedia>
        <ItemContent className="min-w-0 gap-0.5 wrap-anywhere">
          <span className={cn("text-sm", isNew ? "font-medium text-foreground" : "text-muted-foreground")}>
            {line.display}
          </span>
          <span className="text-sm text-muted-foreground">{timeAgo(line.createdAt, now)}</span>
        </ItemContent>
        {isNew && <span aria-label="new" className="mt-1.5 size-2 shrink-0 rounded-full bg-primary" />}
      </Link>
    </Item>
  );
}
